import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { getSignal, getSignalAssignees, getSignalSummary, getSignals, handleSignal } from '../services/signalsApi'
import type { SignalHandlingRequest } from '../services/signalsApi'
import { shouldRetrySignalRequest } from '../components/signals/signalFormat'
import type { SignalListQuery } from '../types/signals'
import { useEtlJobCatalog } from './useEtlJobs'
import { deriveWriteGate } from '../utils/adminGate'
import type { AdminGate } from '../utils/adminGate'
import { errorKind } from '../utils/format'
import { FREQUENT } from '../utils/polling'

/**
 * The signal store's reads, and the people side of a signal.
 *
 * Every key sits under `['observatory', 'signals']`: a halt clear invalidates that prefix
 * (useEtlHalts.clearInvalidationKeys), and so does every handling act here, so the Inbox, the tiles, the badge and
 * the drawer re-read what the server decided — this module never claims anything was resolved.
 *
 * FREQUENT, because a signal is a health counter: the detector writes every 60 s, so polling faster would re-read
 * an unchanged pass, and slower would leave a tile a pass or more behind the store.
 *
 * None of them retries a 503: the list and the detail answer 503 only when a migration is not applied, which no
 * retry fixes. The summary answers 200 with `storeProvisioned: false` in that case, so its 503 is something in front
 * of the API — shown at once rather than after three silent retries.
 */

/** The prefix every signal query sits under, and so what every handling act invalidates. */
export const SIGNALS_QUERY_PREFIX = ['observatory', 'signals'] as const

/** The Overview tiles', the rail badge's, the Health row's and the Inbox header's one shared read. */
export function useSignalSummary() {
  return useQuery({
    queryKey: ['observatory', 'signals', 'summary'],
    queryFn: getSignalSummary,
    refetchInterval: FREQUENT,
    retry: shouldRetrySignalRequest,
  })
}

/**
 * One page of signals. `query` is the key by value, so each filter and page is its own cache entry and a page is
 * never served another filter's rows. No placeholder data for the same reason: while a new filter loads the table
 * shows loading, not the previous filter's signals under the new filter's chips.
 */
export function useSignals(query: SignalListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['observatory', 'signals', 'list', query],
    queryFn: () => getSignals(query),
    refetchInterval: FREQUENT,
    enabled: options?.enabled ?? true,
    retry: shouldRetrySignalRequest,
  })
}

/**
 * One signal and its activity, for the drawer — asked only while an id is set. Polls with the list, so an open
 * drawer does not show a state the table beside it has moved past. A 404 (trimmed by retention, or never existed)
 * is an answer and is not retried.
 */
export function useSignal(id: string | null) {
  return useQuery({
    queryKey: ['observatory', 'signals', 'detail', id],
    queryFn: () => getSignal(id!),
    enabled: id != null && id !== '',
    refetchInterval: FREQUENT,
    retry: shouldRetrySignalRequest,
  })
}

/**
 * Whether this session may handle a signal: the catalogue's server-evaluated `canHandleSignals` (deriveWriteGate).
 * Write controls render ONLY on `admin`; an absent flag is `denied` (this API does not offer it), never `unknown`.
 */
export function useSignalHandlingGate(): AdminGate {
  const { data, isError } = useEtlJobCatalog()
  return deriveWriteGate(data, isError, 'canHandleSignals')
}

/**
 * The people a signal can be handed to — fetched ONLY when the handling gate is `admin` (and `enabled`, e.g. the
 * assign dialog is open). The endpoint is bearer-only: on any other gate the request would be refused, or would not
 * be sent at all, so it is never asked. Not polled: it changes when someone performs an Observatory write.
 */
export function useSignalAssignees(gate: AdminGate, enabled = true) {
  return useQuery({
    queryKey: ['observatory', 'signals', 'assignees'],
    queryFn: getSignalAssignees,
    enabled: gate.state === 'admin' && enabled,
    staleTime: 60_000,
    retry: shouldRetrySignalRequest,
  })
}

/**
 * Whether a failed act leaves the cached signal behind the server: a 409 says somebody acted, or the detector
 * resolved it, since this page last read. Re-reading is what makes the drawer show who.
 */
export function handlingFailureInvalidates(error: unknown): boolean {
  return errorKind(error) === 'conflict'
}

/** Re-reads every signal query — the act can change the list, the summary's counts, and the drawer. */
export function invalidateSignals(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  void queryClient.invalidateQueries({ queryKey: [...SIGNALS_QUERY_PREFIX] })
}

/**
 * Acknowledge, assign or release (bearer-only). On success — and on a 409, which means the cache is behind — it
 * invalidates the `['observatory', 'signals']` prefix and says nothing itself: the caller words the toast from the
 * response (signalHandlingFormat.handlingToast). It never claims a signal resolved; only the detector resolves.
 */
export function useHandleSignal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: SignalHandlingRequest) => handleSignal(request),
    onSuccess: () => invalidateSignals(queryClient),
    onError: (error: unknown) => {
      if (handlingFailureInvalidates(error)) invalidateSignals(queryClient)
    },
  })
}
